import type { AuthUserSummary } from "@/types/auth";

export function getApiBaseUrl(): string {
  return String(
    import.meta.env.VITE_API_BASE_URL ?? import.meta.env.VITE_GENESIS_API_URL ?? "",
  ).replace(/\/+$/, "");
}

export async function fetchLocalLoginStatus(): Promise<{ enabled: boolean }> {
  const baseUrl = getApiBaseUrl();
  if (!baseUrl) {
    return { enabled: false };
  }

  try {
    const response = await fetch(`${baseUrl}/auth/local-login/status`);
    if (!response.ok) {
      return { enabled: false };
    }
    const body = await response.json();
    return { enabled: body?.enabled === true };
  } catch {
    return { enabled: false };
  }
}

export type LocalLoginResult =
  | {
      ok: true;
      accessToken: string;
      email: string;
      expiresIn: number;
    }
  | { ok: false; message: string };

export async function postLocalLogin(
  email: string,
  password: string,
): Promise<LocalLoginResult> {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/auth/local-login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });

  let body: Record<string, unknown> = {};
  try {
    body = await response.json();
  } catch {
    body = {};
  }

  if (!response.ok) {
    const detail = body.detail;
    const message =
      typeof detail === "string"
        ? detail
        : typeof body.message === "string"
          ? body.message
          : response.status === 401
            ? "Invalid email or password"
            : "Sign-in failed. Please try again.";
    return { ok: false, message };
  }

  const accessToken = body.access_token;
  const loginEmail = body.email;
  if (typeof accessToken !== "string" || typeof loginEmail !== "string") {
    return { ok: false, message: "Unexpected login response" };
  }

  const expiresIn =
    typeof body.expires_in === "number" ? body.expires_in : 28800;

  return {
    ok: true,
    accessToken,
    email: loginEmail,
    expiresIn,
  };
}

function normalizeUserSummary(body: unknown): AuthUserSummary | null {
  if (!body || typeof body !== "object") {
    return null;
  }

  const record = body as Record<string, unknown>;
  let raw: Record<string, unknown> | null = null;

  if (typeof record.email === "string") {
    raw = record;
  } else if (record.data && typeof record.data === "object") {
    raw = record.data as Record<string, unknown>;
  }

  if (!raw || typeof raw.email !== "string") {
    return null;
  }

  const displayName =
    typeof raw.display_name === "string"
      ? raw.display_name
      : typeof raw.name === "string"
        ? raw.name
        : raw.email;

  return {
    email: raw.email,
    displayName,
  };
}

/** Signed-in user summary from the API (MSAL or local token). */
export async function fetchAuthUserSummary(
  accessToken: string,
): Promise<AuthUserSummary | null> {
  const baseUrl = getApiBaseUrl();
  if (!baseUrl) {
    return null;
  }

  try {
    const response = await fetch(`${baseUrl}/auth/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!response.ok) {
      return null;
    }

    const body = await response.json();
    return normalizeUserSummary(body);
  } catch {
    return null;
  }
}
